import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from './axiosInstance';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid2';
import Avatar from '@mui/material/Avatar';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import { alpha, useTheme } from '@mui/material/styles';

import { useSnackbar } from '../providers/SnackbarProvider';
import InlineEdit from './ui/InlineEdit';
import CopyToClipboard from './ui/CopyToClipboard';
import ActivityHub from './ActivityHub';
import FileRail from './ui/FileRail';
import SectionCard from './ui/SectionCard';
import MilestoneTracker from './ui/MilestoneTracker';
import StaleBanner from './ui/StaleBanner';
import LostReasonDialog from './ui/LostReasonDialog';
import { DetailSkeleton } from './ui/PageSkeleton';
import { useBreadcrumbs } from './BreadcrumbBar';
import { usePipelineConfig } from '../providers/PipelineConfigProvider';
import {
  accountTypeOptions, accountSegmentOptions,
  organizationTypeOptions, organizationSizeOptions, revenueRangeOptions,
  industryOptions, legalFormOptions,
} from '../constants/accountOptions';

const labelMap = (opts) => Object.fromEntries(opts.map((o) => [o.value, o.label]));
const TYPE_LABELS = labelMap(accountTypeOptions);

const initials = (name) =>
  (name || '?').trim().split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || '?';
const personInitials = (fn, ln) => `${fn?.[0] ?? ''}${ln?.[0] ?? ''}`.toUpperCase() || '?';

/**
 * Unternehmens-Detailseite nach dem Zonen-Bauplan (Konzept 2.3, wie Lead-Detail):
 *   A Kopfzeile   – Identität, ruhig und ohne Knopf-Stapel
 *   B Phasenband  – MilestoneTracker (Account.status) mit andockender Checkliste
 *   C Stammdaten & Ablage (linke Fläche) – Felder, primärer Kontakt, Kontakte,
 *     Projekte, Dateien (der Verknüpft-Bereich ist generisch — künftig auch Cases u. a.)
 *   D Aktivitäten (rechte Fläche, das Rückgrat) – Composer · Geplant · Verlauf
 */
const AccountDetails = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { showSnackbar } = useSnackbar();
  // Pipeline-Konfiguration (B3): Phasen, Checkliste und Labels aus dem Admin
  const { phasesFor, lostFor, statusLabelsFor } = usePipelineConfig();
  const accountPhases = phasesFor('account');
  const accountLost = lostFor('account');
  const accountStatusLabels = statusLabelsFor('account');
  const projectStatusLabels = statusLabelsFor('project');
  const { accountId } = useParams();
  const [account, setAccount] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [availableContacts, setAvailableContacts] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [showAllContacts, setShowAllContacts] = useState(false);
  const [attachmentCount, setAttachmentCount] = useState(null);
  const [addContactDialogOpen, setAddContactDialogOpen] = useState(false);
  const [assignContactPersonDialogOpen, setAssignContactPersonDialogOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState('');
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [lostDialogOpen, setLostDialogOpen] = useState(false);
  const [lastActivityAt, setLastActivityAt] = useState(null);

  useBreadcrumbs(
    account ? [{ label: 'Unternehmen', path: '/accounts' }, { label: `${account.name}` }] : null
  );

  const fetchAccountData = async () => {
    try {
      const response = await axiosInstance.get(`/accounts/${accountId}/`);
      setAccount(response.data);
    } catch (error) {
      console.error('Error fetching account data', error);
    }
  };

  const fetchContacts = async () => {
    try {
      const response = await axiosInstance.get(`/accounts/${accountId}/contacts/`);
      setContacts(response.data);
    } catch (error) {
      console.error('Error fetching contacts', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      fetchAccountData();
      fetchContacts();
      try {
        const [usersRes, availRes, projRes] = await Promise.all([
          axiosInstance.get('/users/'),
          axiosInstance.get('/contacts/'),
          axiosInstance.get('/projects/'),
        ]);
        setUsers(usersRes.data);
        setAvailableContacts(availRes.data);
        setProjects(projRes.data.filter((p) => String(p.account) === String(accountId)));
      } catch (error) {
        console.error('Error loading account context', error);
      }
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  const patchAccount = async (data) => {
    try {
      const response = await axiosInstance.patch(`/accounts/${accountId}/`, data);
      setAccount(response.data);
      showSnackbar('Erfolgreich aktualisiert', 'success');
    } catch (error) {
      console.error('Error updating account:', error);
      showSnackbar(error.response?.data?.account_owner?.[0] || 'Fehler beim Aktualisieren', 'error');
      throw error;
    }
  };
  const handleFieldUpdate = (field, value) => patchAccount({ [field]: value });

  const handleAddContact = async () => {
    try {
      await axiosInstance.post(`/accounts/${accountId}/contacts/`, { contact_id: selectedContact });
      fetchContacts();
      setAddContactDialogOpen(false);
      setSelectedContact('');
      showSnackbar('Kontakt erfolgreich hinzugefügt', 'success');
    } catch (error) {
      console.error('Error adding contact to account:', error);
      showSnackbar('Fehler beim Hinzufügen', 'error');
    }
  };

  const handleAssignContactPerson = async () => {
    try {
      const response = await axiosInstance.patch(`/accounts/${accountId}/`, { contact_person: selectedContact });
      setAccount(response.data);
      setAssignContactPersonDialogOpen(false);
      setSelectedContact('');
      showSnackbar('Primärer Kontakt wurde zugewiesen', 'success');
    } catch (error) {
      console.error('Error assigning contact person:', error);
      showSnackbar('Fehler beim Zuweisen', 'error');
    }
  };

  if (!account) return <DetailSkeleton />;

  const curPhase = accountPhases.find((p) => p.key === account.status);
  const isRejected = !!accountLost && account.status === accountLost.key;
  const doneSet = new Set((account.checklist_state && curPhase && account.checklist_state[curPhase.key]) || []);
  const checklistItems = curPhase ? curPhase.checklist.map((item) => ({ label: item, done: doneSet.has(item) })) : [];
  const toggleChecklistItem = (label) => {
    if (!curPhase) return;
    const next = new Set(doneSet);
    next.has(label) ? next.delete(label) : next.add(label);
    handleFieldUpdate('checklist_state', { ...(account.checklist_state || {}), [curPhase.key]: [...next] });
  };

  const primary = account.contact_person_details;

  // Ein Feld-Zeile (Label + InlineEdit) — unterhalb lg gestapelt (schmale Randspalte)
  const fieldRow = (label, field, opts = {}) => (
    <Box key={field} sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '88px 1fr', md: '1fr', lg: '96px 1fr' },
      alignItems: 'center', columnGap: 1, rowGap: 0, py: { xs: 0.3, md: 0.55, lg: 0.3 },
    }}>
      <Typography variant="subtitle2" color="text.secondary">{label}</Typography>
      <InlineEdit
        value={account?.[field]}
        displayValue={opts.displayValue}
        onSave={(v) => handleFieldUpdate(field, v)}
        selectOptions={opts.select}
      />
    </Box>
  );

  const railLabel = (label, mt = 3) => (
    <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mt, lineHeight: 1.6 }}>{label}</Typography>
  );

  const linkRow = ({ key, icon, title, subtitle, onClick }) => (
    <Box
      key={key}
      role="button" tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1.1, py: 0.6, cursor: 'pointer',
        borderRadius: '8px', px: 0.5, mx: -0.5,
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      {icon}
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }} noWrap>{title}</Typography>
        {subtitle && <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>{subtitle}</Typography>}
      </Box>
    </Box>
  );

  const visibleContacts = showAllContacts ? contacts : contacts.slice(0, 6);

  return (
    <Box sx={{ pb: 3 }}>
      {/* A — Kopfzeile (eigene Fläche, v2.4) */}
      <SectionCard sx={{ mb: 2.5 }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: 'wrap' }}>
        <Avatar sx={{ width: 58, height: 58, bgcolor: 'primary.light', color: 'primary.dark', fontWeight: 800, fontSize: '1.25rem' }}>
          {initials(account.name)}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
            <Typography variant="h4">{account.name}</Typography>
            {account.account_type && (
              <Chip size="small" label={TYPE_LABELS[account.account_type] || account.account_type}
                sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: 'primary.main', fontWeight: 600 }} />
            )}
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5, flexWrap: 'wrap' }}>
            {account.city && (<><PlaceOutlinedIcon sx={{ fontSize: 16 }} />{account.city}</>)}
            {account.phone && (<><PhoneOutlinedIcon sx={{ fontSize: 16, ml: account.city ? 1 : 0 }} />{account.phone}</>)}
            {account.website && (<><LanguageOutlinedIcon sx={{ fontSize: 16, ml: (account.city || account.phone) ? 1 : 0 }} />{account.website}</>)}
          </Typography>
          {account.email && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
              <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere', minWidth: 0 }}>{account.email}</Typography>
              <CopyToClipboard text={account.email} tooltip="E-Mail kopieren" size="small" iconSx={{ fontSize: 14, color: 'text.secondary' }} />
            </Box>
          )}
        </Box>
        <Box sx={{ ml: 'auto' }}>
          <IconButton aria-label="Weitere Aktionen" onClick={(e) => setMenuAnchor(e.currentTarget)}>
            <MoreHorizIcon />
          </IconButton>
          <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
            <MenuItem onClick={() => { setMenuAnchor(null); setLostDialogOpen(true); }} disabled={isRejected || !accountLost} sx={{ color: 'error.main' }}>
              Als abgelehnt markieren
            </MenuItem>
          </Menu>
        </Box>
      </Stack>
      </SectionCard>

      {/* B — Phasenband mit andockender Checkliste */}
      <Box sx={{ mb: 3 }}>
        <MilestoneTracker
          phases={accountPhases}
          currentKey={account.status}
          lost={accountLost}
          successKey={accountPhases[accountPhases.length - 1]?.key}
          sinceDate={account.phase_changed_at}
          lostReason={account.lost_reason}
          checklistItems={checklistItems}
          onToggleChecklistItem={toggleChecklistItem}
          onChangePhase={(key) => handleFieldUpdate('status', key)}
        />
      </Box>

      {/* Inaktivitäts-Warnung — nur bei offenen (nicht abgelehnten) Unternehmen */}
      {!isRejected && (
        <StaleBanner lastActivityAt={lastActivityAt}
          hintDays={curPhase?.staleHintDays} warnDays={curPhase?.staleWarnDays} />
      )}

      {/* Themen-Flächen auf getöntem Grund — wie auf der Lead-Detailseite */}
      <Grid container spacing={2.5} alignItems="flex-start">
        {/* C — Stammdaten & Ablage */}
        <Grid size={{ xs: 12, md: 4, lg: 3.5 }} order={{ xs: 2, md: 1 }}
          sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <SectionCard>
          <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.6 }}>Stammdaten</Typography>
          {fieldRow('Name', 'name')}
          {fieldRow('Inhaber', 'account_owner', {
            displayValue: account?.account_owner_username,
            select: users.map((u) => ({
              value: u.id,
              label: u.first_name && u.last_name ? `${u.first_name} ${u.last_name}` : u.email,
            })),
          })}
          {fieldRow('Branche', 'industry', { select: industryOptions })}
          {fieldRow('Typ', 'account_type', { select: accountTypeOptions })}
          {fieldRow('Segment', 'account_segment', { select: accountSegmentOptions })}
          {showAll && fieldRow('Rechtsform', 'legal_form', { select: legalFormOptions })}
          {showAll && fieldRow('Org.-Typ', 'organization_type', { select: organizationTypeOptions })}
          {showAll && fieldRow('Größe', 'organization_size', { select: organizationSizeOptions })}
          {showAll && fieldRow('Umsatz', 'revenue_range', { select: revenueRangeOptions })}

          {railLabel('Adresse')}
          {fieldRow('Straße', 'street')}
          {fieldRow('PLZ', 'zip_code')}
          {fieldRow('Stadt', 'city')}

          {railLabel('Erreichbarkeit')}
          {fieldRow('E-Mail', 'email')}
          {fieldRow('Telefon', 'phone')}
          {fieldRow('Webseite', 'website')}

          <Button size="small" onClick={() => setShowAll((v) => !v)} sx={{ mt: 1, px: 0.5 }}>
            {showAll ? 'Weitere Felder ausblenden ▴' : 'Alle Felder anzeigen ▾'}
          </Button>
          </SectionCard>

          {/* Ansprechpartner-Karte (v2.4, nach User-Mockup) */}
          <SectionCard>
          {railLabel('Primärer Kontakt', 0)}
          {primary ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {linkRow({
                  key: 'primary',
                  icon: (
                    <Avatar sx={{ width: 28, height: 28, fontSize: '0.72rem', fontWeight: 700, bgcolor: 'primary.light', color: 'primary.dark' }}>
                      {personInitials(primary.first_name, primary.last_name)}
                    </Avatar>
                  ),
                  title: `${primary.first_name} ${primary.last_name}`,
                  subtitle: [primary.position, primary.email].filter(Boolean).join(' · '),
                  onClick: () => navigate(`/contacts/${primary.id}`),
                })}
              </Box>
              <Button size="small" sx={{ px: 0.5, flex: 'none' }} onClick={() => setAssignContactPersonDialogOpen(true)}>Ändern</Button>
            </Box>
          ) : (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Noch nicht festgelegt — wer ist die Hauptansprechperson?
              </Typography>
              <Button size="small" sx={{ px: 0.5, mt: 0.25 }} onClick={() => setAssignContactPersonDialogOpen(true)}>+ Zuweisen</Button>
            </Box>
          )}

          {railLabel(`Kontakte (${contacts.length})`)}
          {visibleContacts.map((c) => linkRow({
            key: `c-${c.id}`,
            icon: (
              <Avatar sx={{ width: 28, height: 28, fontSize: '0.72rem', fontWeight: 700, bgcolor: alpha(theme.palette.secondary.main, 0.15), color: 'secondary.main' }}>
                {personInitials(c.first_name, c.last_name)}
              </Avatar>
            ),
            title: `${c.first_name} ${c.last_name}`,
            subtitle: c.position || c.email || '',
            onClick: () => navigate(`/contacts/${c.id}`),
          }))}
          {contacts.length === 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Noch keine Kontakte zugeordnet.</Typography>
          )}
          {contacts.length > 6 && (
            <Button size="small" sx={{ px: 0.5, mt: 0.25 }} onClick={() => setShowAllContacts((v) => !v)}>
              {showAllContacts ? 'Weniger anzeigen ▴' : `Alle ${contacts.length} anzeigen ▾`}
            </Button>
          )}
          {/* Gestricheltes „Hinzufügen" — ruhiges Signal für „hier darf etwas entstehen" */}
          <Button fullWidth size="small" onClick={() => setAddContactDialogOpen(true)} sx={{
            mt: 1.5, py: 0.75, color: 'text.secondary', bgcolor: 'transparent',
            border: '1px dashed', borderColor: 'divider',
            '&:hover': { bgcolor: 'action.hover', borderColor: 'primary.main', color: 'primary.main', borderStyle: 'dashed' },
          }}>
            + Weiteren Kontakt hinzufügen
          </Button>
          </SectionCard>

          <SectionCard>
          {railLabel(`Projekte (${projects.length})`, 0)}
          {projects.map((p) => linkRow({
            key: `p-${p.id}`,
            icon: <WorkOutlineIcon sx={{ fontSize: 20, color: 'text.secondary' }} />,
            title: p.name,
            subtitle: projectStatusLabels[p.status] || p.status,
            onClick: () => navigate(`/projects/${p.id}`),
          }))}
          {projects.length === 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Noch keine Projekte mit diesem Unternehmen.</Typography>
          )}

          </SectionCard>

          <SectionCard>
          {railLabel(attachmentCount != null ? `Dateien (${attachmentCount})` : 'Dateien', 0)}
          <FileRail relatedType="account" relatedId={account.id} onCountChange={setAttachmentCount} />
          </SectionCard>
        </Grid>

        {/* D — Aktivitäten (das Rückgrat) */}
        <Grid size={{ xs: 12, md: 8, lg: 8.5 }} order={{ xs: 1, md: 2 }} sx={{ minWidth: 0 }}>
          <ActivityHub
            relatedType="account"
            relatedId={accountId}
            phaseChangedAt={account.phase_changed_at}
            phaseLabel={curPhase ? curPhase.label : (isRejected ? accountLost.label : account.status)}
            statusHistory={account.status_history}
            statusLabels={accountStatusLabels}
            onMeta={({ lastActivityAt: la }) => setLastActivityAt(la)}
          />
        </Grid>
      </Grid>

      <LostReasonDialog
        open={lostDialogOpen}
        onClose={() => setLostDialogOpen(false)}
        onConfirm={(reason) => { setLostDialogOpen(false); patchAccount({ status: accountLost?.key, lost_reason: reason }); }}
      />

      {/* Kontakt-hinzufügen-Dialog */}
      <Dialog open={addContactDialogOpen} onClose={() => setAddContactDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Kontakt zum Unternehmen hinzufügen</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Kontakt auswählen</InputLabel>
              <Select value={selectedContact} onChange={(e) => setSelectedContact(e.target.value)} label="Kontakt auswählen">
                {availableContacts
                  .filter((contact) => !contacts.some((c) => c.id === contact.id))
                  .map((contact) => (
                    <MenuItem key={contact.id} value={contact.id}>
                      {contact.first_name && contact.last_name ? `${contact.first_name} ${contact.last_name}` : contact.email || 'Unbenannter Kontakt'}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAddContactDialogOpen(false)}>Abbrechen</Button>
          <Button onClick={handleAddContact} variant="contained" disabled={!selectedContact}>Hinzufügen</Button>
        </DialogActions>
      </Dialog>

      {/* Primären-Kontakt-Dialog */}
      <Dialog open={assignContactPersonDialogOpen} onClose={() => setAssignContactPersonDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Primären Kontakt zuweisen</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Primären Kontakt auswählen</InputLabel>
              <Select value={selectedContact} onChange={(e) => setSelectedContact(e.target.value)} label="Primären Kontakt auswählen">
                {contacts.map((contact) => (
                  <MenuItem key={contact.id} value={contact.id}>
                    {contact.first_name && contact.last_name ? `${contact.first_name} ${contact.last_name}` : contact.email || 'Unbenannter Kontakt'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAssignContactPersonDialogOpen(false)}>Abbrechen</Button>
          <Button onClick={handleAssignContactPerson} variant="contained" disabled={!selectedContact}>Zuweisen</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AccountDetails;
